/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DonutChart } from 'src/core/components/DonutChart';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { ChartAxisData } from 'src/core/types/chart';
import { Legend } from 'src/luis/projects/analytics/components/OverallPredictionsCard/OverallChart/Legend/Legend';
import { AnalyticsDropdown } from 'src/luis/projects/analytics/components/TrainingEvaluation/AnalyticsDropdown';

export type OverallChartProps = {
    /**
     * The version ids tht can be compared with the current one.
     */
    comparableVersionIds: string[];

    /**
     *  callback to be invoked whenever the compared version is changed.
     */
    onVersionComparisonChange: (versionId: string) => void;
};

const messages = defineMessages({
    compareVersionsLabel: {
        id: 'analytics.overallChart.compareVersionsLabel',
        defaultMessage: 'Show change from up to date trained version:'
    },
    correctLabel: {
        id: 'analytics.overallChart.correctLabel',
        defaultMessage: 'correct'
    }
});

const { styleComponent } = getStylist('Dashboard');
const dashboardTheme = getScopedTheme('Dashboard');

const StyledLabel = styleComponent<ILabelProps>(Label)('StyledLabel', theme => ({
    fontSize: theme.labelFontSize,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing
}));

const DropdownContainer = styleComponent<IStackProps>(Stack)('DropdownContainer', theme => ({
    marginTop: 'auto',
    paddingTop: theme.versionComparisonSpacing
}));

const Chart = styleComponent<IStackProps>(Stack)('Chart', { marginTop: 'auto', marginBottom: 'auto' });

/**
 * The donut chart section with the legend items.
 */
export const OverallChart = observer((props: OverallChartProps) => {
    const { comparableVersionIds, onVersionComparisonChange } = props;
    const [selectedComparedVersionId, setSelectedComparedVersionId] = React.useState('none');
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const eventLoggerRef = React.useRef(getEventLogger('OverallChart'));

    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;

    const isComparisonActive = selectedComparedVersionId !== 'none';
    const isComparisonReady = isComparisonActive && analyticsStore.comparedVersionDifferences;

    const upToDateVersionIds: IDropdownOption[] = [
        { key: 'none', text: '—', selected: true },
        ...comparableVersionIds.map(id => ({ key: id, text: id }))
    ];

    const correctPercentage = analyticsStore.utterancesCount
        ? (analyticsStore.correctUtterancesCount * 100) / analyticsStore.utterancesCount
        : 0;
    const incorrectPercentage = analyticsStore.utterancesCount
        ? (analyticsStore.incorrectUtterancesCount * 100) / analyticsStore.utterancesCount
        : 0;
    const unclearPercentage = analyticsStore.utterancesCount
        ? (analyticsStore.unclearUtterancesCount * 100) / analyticsStore.utterancesCount
        : 0;

    const donutChartTitle = `${correctPercentage.toFixed(1)}%`;

    const xData: ChartAxisData<string> = {
        label: 'Type',
        data: ['Correctly predicted', 'Unclear', 'Incorrectly predicted']
    };

    const yData: ChartAxisData<number> = {
        label: 'Ratio',
        data: [correctPercentage, unclearPercentage, incorrectPercentage]
    };

    return (
        <Stack verticalFill verticalAlign="center" tokens={{ padding: 'auto' }}>
            <Chart tokens={{ childrenGap: 20 }} horizontalAlign="center">
                <DonutChart
                    xData={xData}
                    yData={yData}
                    donutTitle={donutChartTitle}
                    donutSubtitle={localize(messages.correctLabel)}
                    size="large"
                />
                <Legend
                    inComparisonMode={isComparisonActive}
                    correctPercentage={correctPercentage}
                    incorrectPercentage={incorrectPercentage}
                    unclearPercentage={unclearPercentage}
                    correctPercentageDiff={isComparisonReady ? analyticsStore.comparedVersionDifferences.correctAccuracy : null}
                    incorrectPercentageDiff={isComparisonReady ? analyticsStore.comparedVersionDifferences.incorrectAccuracy : null}
                    unclearPercentageDiff={isComparisonReady ? analyticsStore.comparedVersionDifferences.unclearAccuracy : null}
                    numIntents={analyticsStore.intentsCount}
                    numUtterances={analyticsStore.utterancesCount}
                    numEntities={analyticsStore.entitiesCount}
                    numIntentsDiff={isComparisonReady ? analyticsStore.comparedVersionDifferences.numIntents : null}
                    numEntitiesDiff={isComparisonReady ? analyticsStore.comparedVersionDifferences.numEntities : null}
                    numUtterancesDiff={isComparisonReady ? analyticsStore.comparedVersionDifferences.numUtterances : null}
                />
            </Chart>
            <DropdownContainer horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                <StyledLabel>{localize(messages.compareVersionsLabel)}</StyledLabel>
                <AnalyticsDropdown
                    options={upToDateVersionIds}
                    disabled={comparableVersionIds.length === 0}
                    onChange={(_, option) => {
                        eventLoggerRef.current.log('TrainedVersionDropdownSelectionChange');
                        onVersionComparisonChange(option.key as string);
                        setSelectedComparedVersionId(option.key as string);
                    }}
                    onRenderCaretDown={() => (
                        <Icon
                            iconName="CaretDownSolid8"
                            style={{ color: comparableVersionIds.length ? 'none' : dashboardTheme.disabledColor }}
                        />
                    )}
                />
            </DropdownContainer>
        </Stack>
    );
});
